import React from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { ScrollToTop, SignUp, VerifyOTP, Login, ForgetPassword } from './components';
import { LandingPage, AppSelectionPage } from './pages';
import SuperAdminHome from './pages/SuperAdminHome';
import SuperAdminUserAnalytics from './pages/SuperAdminUserAnalytics';
import SuperAdminUnpaidUsers from './pages/SuperAdminUnpaidUsers';
import SuperAdminUserOnline from './pages/SuperAdminUserOnline';
import SuperAdminChitFundAnalytics from './pages/SuperAdminChitFundAnalytics';
import SuperAdminDailyFinanceAnalytics from './pages/SuperAdminDailyFinanceAnalytics';
import SuperAdminBilling from './pages/SuperAdminBilling';
import SuperAdminPlanFees from './pages/SuperAdminPlanFees';
import SuperAdminJobs from './pages/SuperAdminJobs';
import { UserProvider } from './context/user_context';
import { PlatformAccessProvider } from './context/platformAccess_context';
import { LedgerAccountProvider } from './context/ledgerAccount_context';
import PlatformEmployeesPage from './pages/PlatformEmployeesPage';

// New Business App Layouts
import ChitFundUserLayout from './components/chitFund/ChitFundUserLayout';
import ChitFundSubscriberLayout from './components/chitFund/ChitFundSubscriberLayout';
import ChitFundManagerLayout from './components/chitFund/ChitFundManagerLayout';
import ChitFundAccountantLayout from './components/chitFund/ChitFundAccountantLayout';
import ChitFundCollectorLayout from './components/chitFund/ChitFundCollectorLayout';
import DailyCollectionAdminLayout from './components/dailyCollection/DailyCollectionAdminLayout';
import DailyCollectionCustomerLayout from './components/dailyCollection/DailyCollectionCustomerLayout';
import DailyCollectionCollectorLayout from './components/dailyCollection/DailyCollectionCollectorLayout';
import PersonalLoanAdminLayout from './components/personalLoan/PersonalLoanAdminLayout';
import PersonalFinanceLayout from './components/personalFinance/PersonalFinanceLayout';
import DocumentsLayout from './components/documents/DocumentsLayout';
import VehicleParkingLayout from './components/vehicleParking/VehicleParkingLayout';
import VehicleParkingCustomerLayout from './components/vehicleParking/VehicleParkingCustomerLayout';
import DeepavaliAdminLayout, { DeepavaliCollectorLayout } from './components/deepavaliChits/DeepavaliAdminLayout';
import VehicleFinanceAdminLayout from './components/vehicleFinance/VehicleFinanceAdminLayout';
import VehicleFinanceManagerLayout from './components/vehicleFinance/VehicleFinanceManagerLayout';
import VehicleFinanceCollectorLayout from './components/vehicleFinance/VehicleFinanceCollectorLayout';
import VehicleFinanceCustomerLayout from './components/vehicleFinance/VehicleFinanceCustomerLayout';
import RentalManagementAdminLayout from './components/rentalManagement/RentalManagementAdminLayout';
import RentalManagementCustomerLayout from './components/rentalManagement/RentalManagementCustomerLayout';
import HostelManagementAdminLayout, {
  HostelManagementManagerLayout,
  HostelManagementReceptionistLayout,
} from './components/hostelManagement/HostelManagementAdminLayout';
import HostelManagementResidentLayout from './components/hostelManagement/HostelManagementResidentLayout';
import HostelManagementKitchenLayout from './components/hostelManagement/HostelManagementKitchenLayout';
import { HM_KITCHENSTAFF_BASE_PATH } from './components/hostelManagement/hostelManagementMenuItems';
import MuttonStallAdminLayout, {
  MuttonStallManagerLayout,
  MuttonStallSalesmanLayout,
} from './components/muttonStall/MuttonStallAdminLayout';
import MuttonStallCustomerLayout from './components/muttonStall/MuttonStallCustomerLayout';
import MuttonStallPublicLandingPage from './pages/muttonStall/MuttonStallPublicLandingPage';
import HospitalManagementAdminLayout, {
  HospitalManagementManagerLayout,
  HospitalManagementReceptionistLayout,
  HospitalManagementPharmacistLayout,
  HospitalManagementDoctorLayout,
  HospitalManagementNurseLayout,
  HospitalManagementCompounderLayout,
  HospitalManagementKitchenLayout,
} from './components/hospitalManagement/HospitalManagementAdminLayout';
import HospitalPatientPortalLogin from './pages/hospitalManagement/patientPortal/HospitalPatientPortalLogin';
import HospitalPatientPortalHome from './pages/hospitalManagement/patientPortal/HospitalPatientPortalHome';
import HospitalManagementDoctorLogin from './pages/hospitalManagement/HospitalManagementDoctorLogin';
import HospitalManagementStaffLogin from './pages/hospitalManagement/HospitalManagementStaffLogin';

// Legacy layouts for backward compatibility (can be removed later)
import CollectorLayout from './components/collector/CollectorLayout';
import DailyCollectionLayout from './components/dailyCollection/DailyCollectionLayout';


import PrivateRoute from './pages/PrivateRoute';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';


function App() {
    return (
        <UserProvider>
            <PlatformAccessProvider>
                <LedgerAccountProvider>
                    <Router>
                    <ScrollToTop />
                    <Switch>
                        {/* Super Admin Portal */}
                        <PrivateRoute exact path="/super-admin/user-analytics" component={SuperAdminUserAnalytics} />
                        <PrivateRoute exact path="/super-admin/unpaid-users" component={SuperAdminUnpaidUsers} />
                        <PrivateRoute exact path="/super-admin/user-online" component={SuperAdminUserOnline} />
                        <PrivateRoute exact path="/super-admin/billing" component={SuperAdminBilling} />
                        <PrivateRoute exact path="/super-admin/plan-fees" component={SuperAdminPlanFees} />
                        <PrivateRoute exact path="/super-admin/jobs" component={SuperAdminJobs} />
                        <PrivateRoute exact path="/super-admin/chit-fund-analytics" component={SuperAdminChitFundAnalytics} />
                        <PrivateRoute exact path="/super-admin/daily-finance-analytics" component={SuperAdminDailyFinanceAnalytics} />
                        <PrivateRoute exact path="/super-admin" component={SuperAdminHome} />
                        <Route path="/super-admin/apps/:appId" render={() => <Redirect to="/super-admin" />} />

                        {/* App Selection Page - Entry point */}
                        <PrivateRoute path="/app-selection" component={AppSelectionPage} />
                        <PrivateRoute exact path="/platform/employees" component={PlatformEmployeesPage} />


                        {/* Chit Fund App Routes */}
                        <Route path="/chit-fund/user" component={ChitFundUserLayout} />
                        <Route path="/chit-fund/subscriber" component={ChitFundSubscriberLayout} />
                        <Route path="/chit-fund/manager" component={ChitFundManagerLayout} />
                        <Route path="/chit-fund/accountant" component={ChitFundAccountantLayout} />
                        <Route path="/chit-fund/collector" component={ChitFundCollectorLayout} />

                        {/* Daily Collection App Routes - Specific routes first */}
                        <Route path="/daily-collection/user" component={DailyCollectionAdminLayout} />
                        <Route path="/daily-collection/customer" component={DailyCollectionCustomerLayout} />
                        <Route path="/daily-collection/collector" component={DailyCollectionCollectorLayout} />
                        {/* Redirect old routes to new structure */}
                        <Route path="/daily-collection/home" render={() => <Redirect to="/daily-collection/user/home" />} />

                        {/* Two Wheeler Finance - redirect to Vehicle Finance */}
                        <Route path="/two-wheeler-finance" render={() => <Redirect to="/vehicle-finance/user/dashboard" />} />

                        {/* Vehicle Finance App Routes */}
                        <Route
                            path="/vehicle-finance/user/collector"
                            render={() => <Redirect to="/login" />}
                        />
                        <Route path="/vehicle-finance/collector" component={VehicleFinanceCollectorLayout} />
                        <Route path="/vehicle-finance/customer" component={VehicleFinanceCustomerLayout} />
                        <Route path="/vehicle-finance/user" component={VehicleFinanceAdminLayout} />
                        <Route path="/vehicle-finance/manager" component={VehicleFinanceManagerLayout} />

                        {/* Personal Loan App Routes */}
                        <Route path="/personal-loan/user" component={PersonalLoanAdminLayout} />

                        {/* Personal Finance App Routes */}
                        <Route path="/personal-finance" component={PersonalFinanceLayout} />
                        <Route path="/vehicle-parking/customer" component={VehicleParkingCustomerLayout} />
                        <Route path="/vehicle-parking" component={VehicleParkingLayout} />
                        <Route path="/deepavali-chits/collector" component={DeepavaliCollectorLayout} />
                        <Route path="/deepavali-chits/user" component={DeepavaliAdminLayout} />
                        <Route path="/documents-box" component={DocumentsLayout} />
                        <Route
                            path="/documents"
                            render={({ location }) => (
                                <Redirect
                                    to={`${location.pathname.replace(/^\/documents(?=\/|$)/, '/documents-box')}${location.search || ''}`}
                                />
                            )}
                        />

                        {/* Rental Management App Routes */}
                        <Route path="/rental-management/user" component={RentalManagementAdminLayout} />
                        <Route path="/rental-management/customer" component={RentalManagementCustomerLayout} />

                        {/* Hostel Management App Routes (role bases like Chit Fund) */}
                        <Route path="/hostel-management/user" component={HostelManagementAdminLayout} />
                        <Route path="/hostel-management/manager" component={HostelManagementManagerLayout} />
                        <Route path="/hostel-management/receptionist" component={HostelManagementReceptionistLayout} />
                        <Route path="/hostel-management/resident" component={HostelManagementResidentLayout} />
                        <Route path="/hostel-management/kitchenstaff" component={HostelManagementKitchenLayout} />
                        {/* Legacy kitchen → kitchenstaff */}
                        <Route
                            path="/hostel-management/kitchen"
                            render={({ location }) => (
                                <Redirect
                                    to={`${HM_KITCHENSTAFF_BASE_PATH}${location.pathname.replace('/hostel-management/kitchen', '') || '/food-report'}${location.search || ''}`}
                                />
                            )}
                        />

                        {/* Mutton Stall POS */}
                        <Route path="/mutton-stall/order/:orderToken" render={() => <Redirect to="/mutton-stall/customer/dashboard" />} />
                        <Route path="/mutton-stall/user" component={MuttonStallAdminLayout} />
                        <Route path="/mutton-stall/manager" component={MuttonStallManagerLayout} />
                        <Route path="/mutton-stall/salesman" component={MuttonStallSalesmanLayout} />
                        <Route path="/mutton-stall/customer" component={MuttonStallCustomerLayout} />

                        {/* Hospital Management App Routes */}
                        <Route exact path="/hospital-management/patient" component={HospitalPatientPortalLogin} />
                        <Route path="/hospital-management/patient" component={HospitalPatientPortalHome} />
                        <Route exact path="/hospital-management/doctor/login" component={HospitalManagementDoctorLogin} />
                        <Route exact path="/hospital-management/manager/login" component={HospitalManagementStaffLogin} />
                        <Route exact path="/hospital-management/receptionist/login" component={HospitalManagementStaffLogin} />
                        <Route exact path="/hospital-management/pharmacist/login" component={HospitalManagementStaffLogin} />
                        <Route exact path="/hospital-management/nurse/login" component={HospitalManagementStaffLogin} />
                        <Route exact path="/hospital-management/compounder/login" component={HospitalManagementStaffLogin} />
                        <Route exact path="/hospital-management/kitchen/login" component={HospitalManagementStaffLogin} />
                        <Route path="/hospital-management/user" component={HospitalManagementAdminLayout} />
                        <Route path="/hospital-management/manager" component={HospitalManagementManagerLayout} />
                        <Route path="/hospital-management/receptionist" component={HospitalManagementReceptionistLayout} />
                        <Route path="/hospital-management/pharmacist" component={HospitalManagementPharmacistLayout} />
                        <Route path="/hospital-management/doctor" component={HospitalManagementDoctorLayout} />
                        <Route path="/hospital-management/nurse" component={HospitalManagementNurseLayout} />
                        <Route path="/hospital-management/compounder" component={HospitalManagementCompounderLayout} />
                        <Route path="/hospital-management/kitchen" component={HospitalManagementKitchenLayout} />

                        {/* Legacy Routes for Backward Compatibility */}
                        <Route path="/customer" render={() => <Redirect to="/chit-fund/subscriber" />} />
                        <Route path="/collector" component={CollectorLayout} />
                        <Route path="/daily-collection" component={DailyCollectionLayout} />

                        {/* Legacy Chit Fund Routes - Redirect to new structure */}
                        <Route path="/subscriber" render={() => <Redirect to="/chit-fund/subscriber" />} />
                        <Route path="/subscribers" render={() => <Redirect to="/chit-fund/user/subscribers" />} />
                        <Route path="/receivables" render={() => <Redirect to="/chit-fund/user/receivables" />} />
                        <Route path="/payables" render={() => <Redirect to="/chit-fund/user/payables" />} />
                        <Route path="/groups" render={() => <Redirect to="/chit-fund/user/groups" />} />
                        <Route path="/companies" render={() => <Redirect to="/chit-fund/user/companies" />} />
                        <Route path="/employees" render={() => <Redirect to="/chit-fund/user/employees" />} />
                        <Route path="/dashboard" render={() => <Redirect to="/chit-fund/user/dashboard" />} />
                        <Route path="/ledger" render={() => <Redirect to="/chit-fund/user/ledger" />} />
                        <Route path="/products" render={() => <Redirect to="/chit-fund/user/products" />} />
                        <Route path="/billing" render={() => <Redirect to="/chit-fund/user/billing" />} />
                        <Route path="/my-billing" render={() => <Redirect to="/chit-fund/user/billing" />} />
                        <Route path="/home" render={() => <Redirect to="/chit-fund/user/home" />} />
                        <Route path="/startagroup" render={() => <Redirect to="/chit-fund/user/startagroup" />} />
                        <Route path="/help" render={() => <Redirect to="/chit-fund/user/help" />} />
                        <Route path="/Faq" render={() => <Redirect to="/chit-fund/user/Faq" />} />
                        <Route path="/addgroupsubscriber/:groupId" render={(props) => <Redirect to={`/chit-fund/user/addgroupsubscriber/${props.match.params.groupId}`} />} />
                        <Route path="/addgroupsubscriber/:groupId/addnew" render={(props) => <Redirect to={`/chit-fund/user/addgroupsubscriber/${props.match.params.groupId}/addnew`} />} />
                        <Route path="/addgroupsubscriber/:groupId/addcompanysubcriber" render={(props) => <Redirect to={`/chit-fund/user/addgroupsubscriber/${props.match.params.groupId}/addcompanysubcriber`} />} />
                        <Route path="/addcompanymultisubscriber/:membershipId" render={(props) => <Redirect to={`/chit-fund/user/addcompanymultisubscriber/${props.match.params.membershipId}`} />} />
                        <Route path="/groups/:groupId" render={(props) => <Redirect to={`/chit-fund/user/groups/${props.match.params.groupId}`} />} />
                        <Route path="/admin-settings" render={() => <Redirect to="/chit-fund/user/adminsettings" />} />
                        <Route path="/personal-settings" render={() => <Redirect to="/chit-fund/user/personalsettings" />} />

                        {/* Public Routes */}
                        <Route path="/" exact component={LandingPage} />
                        <Route path="/signup" component={SignUp} />
                        <Route path="/verify-otp" component={VerifyOTP} />
                        <Route path="/login" component={Login} />
                        <Route path="/forget-password" component={ForgetPassword} />

                        {/* Mutton Stall public landing: /{stall-name} */}
                        <Route exact path="/stall/:stallSlug" component={MuttonStallPublicLandingPage} />
                        <Route exact path="/:stallSlug" component={MuttonStallPublicLandingPage} />

                        {/* Default redirect to app selection */}
                        <Route path="/">
                            <LandingPage />
                        </Route>
                    </Switch>
                    <WhatsAppFloatingButton />
                    </Router>
                </LedgerAccountProvider>
            </PlatformAccessProvider>
        </UserProvider>
    );
}

export default App;